import { useState, Fragment, memo, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { useEditor } from './EditorContext';
import { MoveItemDialog } from './MoveItemDialog';
import { useTenant } from '../auth/TenantProvider';
import { ConfirmDialog } from '../components/ConfirmDialog';
import {
  RecurringDeleteDialog,
  type RecurringDeleteScope,
} from '../components/RecurringDeleteDialog';
import {
  getAccentColor,
  type Item,
  type Subitem,
  type MeetingType,
} from '../types/agenda';
import {
  occurrenceOverride,
  skipOccurrence,
  includeOccurrence,
  clearOccurrenceOverride,
} from '../util/recurrence';
import { computeItemStartMinutes, formatMinutes } from '../util/time';
import './item-list.css';

export function ItemListPanel() {
  const { draft, addItem, removeItem, moveItem, updateItem, reorder, select, selectedIndex } = useEditor();
  const [confirmDel, setConfirmDel] = useState<number | null>(null);
  const [moveFor, setMoveFor] = useState<number | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const starts = computeItemStartMinutes(draft);
  const ids = draft.items.map((item) => item.id);

  // Stable callbacks for row interactions — passed to memoized SortableItemRow
  const handleRowSelect = useCallback((index: number) => {
    select(index);
  }, [select]);

  const handleRowDelete = useCallback((index: number) => {
    setConfirmDel(index);
  }, []);

  const handleRowMove = useCallback((index: number) => {
    setMoveFor(index);
  }, []);

  const handleRowInclude = useCallback((index: number) => {
    const item = draft.items[index];
    if (item) updateItem(index, includeOccurrence(item, draft.date));
  }, [draft.items, draft.date, updateItem]);

  const handleRowClearOverride = useCallback((index: number) => {
    const item = draft.items[index];
    if (item) updateItem(index, clearOccurrenceOverride(item, draft.date));
  }, [draft.items, draft.date, updateItem]);

  // Gap (positive) or overlap (negative) in minutes between adjacent items.
  // null when either item lacks a computable start or the previous has no duration.
  const gaps: (number | null)[] = draft.items.map((item, i) => {
    if (i === 0) return null;
    const prevStart = starts[i - 1];
    const prevDur = draft.items[i - 1].timeRange?.duration ?? 0;
    const curStart = starts[i];
    const prevIndependent = draft.items[i - 1].timeRange?.independent ?? false;
    const curIndependent = item.timeRange?.independent ?? false;
    if (prevStart === null || curStart === null || prevDur === 0) return null;
    if (prevIndependent || curIndependent) return null;
    const diff = curStart - (prevStart + prevDur);
    return diff !== 0 ? diff : null;
  });

  const onDragStart = (e: DragStartEvent) => {
    setActiveId(String(e.active.id));
  };

  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from !== -1 && to !== -1) reorder(from, to);
  };

  const activeIndex = activeId ? ids.indexOf(activeId) : -1;
  const activeItem = activeIndex !== -1 ? draft.items[activeIndex] : null;

  const realItemCount = draft.items.filter((i) => i.type !== 'insertion-point').length;

  const handleAdd = () => {
    const idx = addItem();
    if (idx >= 0) select(idx);
  };

  return (
    <div className="item-list-panel">
      <div className="item-list-scroll">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            {realItemCount === 0 && (
              <p className="item-list-empty">No items yet.</p>
            )}
            {draft.items.map((item, i) => (
              <Fragment key={ids[i]}>
                {gaps[i] !== null && <GapBar minutes={gaps[i]!} />}
                {item.type === 'insertion-point' ? (
                  <SortableInsertionPointRow id={ids[i]} onAdd={handleAdd} />
                ) : (
                  <SortableItemRow
                    id={ids[i]}
                    item={item}
                    index={i}
                    meetingType={draft.meetingType}
                    startMin={starts[i]}
                    agendaDate={draft.date}
                    selected={selectedIndex === i}
                    onSelect={handleRowSelect}
                    onDelete={handleRowDelete}
                    onMove={handleRowMove}
                    onInclude={handleRowInclude}
                    onClearOverride={handleRowClearOverride}
                    subitems={item.subitems ?? []}
                  />
                )}
              </Fragment>
            ))}
          </SortableContext>
          <DragOverlay dropAnimation={null}>
            {activeItem && activeIndex !== -1 ? (
              <div className="drag-overlay-item">
                {activeItem.type === 'insertion-point' ? (
                  <InsertionPointRowOverlay />
                ) : (
                  <ItemRowOverlay
                    item={activeItem}
                    meetingType={draft.meetingType}
                    startMin={starts[activeIndex]}
                    subitems={activeItem.subitems ?? []}
                  />
                )}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      <ConfirmDialog
        open={confirmDel !== null && !draft.items[confirmDel]?.recurring}
        title="Delete item?"
        message={
          confirmDel !== null ? `Delete "${draft.items[confirmDel]?.title}"?` : ''
        }
        confirmLabel="Delete"
        danger
        onConfirm={() => {
          if (confirmDel !== null) removeItem(confirmDel);
          setConfirmDel(null);
        }}
        onClose={() => setConfirmDel(null)}
      />

      <RecurringDeleteDialog
        open={confirmDel !== null && !!draft.items[confirmDel]?.recurring}
        title={confirmDel !== null ? draft.items[confirmDel]?.title : undefined}
        onConfirm={(scope: RecurringDeleteScope) => {
          if (confirmDel !== null) {
            const item = draft.items[confirmDel];
            if (scope === 'all') {
              removeItem(confirmDel);
            } else if (item) {
              updateItem(confirmDel, skipOccurrence(item, draft.date));
            }
          }
          setConfirmDel(null);
        }}
        onClose={() => setConfirmDel(null)}
      />

      <MoveItemDialog
        open={moveFor !== null}
        itemTitle={moveFor !== null ? (draft.items[moveFor]?.title ?? '') : ''}
        currentAgendaId={draft.id}
        meetingType={draft.meetingType}
        onSelect={(destId) => {
          if (moveFor !== null) void moveItem(moveFor, destId);
          setMoveFor(null);
        }}
        onClose={() => setMoveFor(null)}
      />
    </div>
  );
}

function SortableInsertionPointRow({ id, onAdd }: { id: string; onAdd: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={
        isDragging
          ? { visibility: 'hidden' }
          : { transform: CSS.Translate.toString(transform), transition }
      }
    >
      <div className="insertion-point-row">
        <button
          className="item-drag"
          aria-label="Reorder insertion point"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
        >
          <DragIcon />
        </button>
        <button className="insertion-point-btn" onClick={onAdd}>
          + Add Item
        </button>
      </div>
    </div>
  );
}

function InsertionPointRowOverlay() {
  return (
    <div className="insertion-point-row">
      <button className="item-drag" tabIndex={-1}>
        <DragIcon />
      </button>
      <span className="insertion-point-btn">+ Add Item</span>
    </div>
  );
}

interface RowProps {
  id: string;
  item: Item;
  index: number;
  meetingType: MeetingType;
  startMin: number | null;
  agendaDate: string;
  selected: boolean;
  onSelect: (index: number) => void;
  onDelete: (index: number) => void;
  onMove: (index: number) => void;
  onInclude: (index: number) => void;
  onClearOverride: (index: number) => void;
  subitems: Subitem[];
}

function SortableItemRowComponent({
  id,
  item,
  index,
  meetingType,
  startMin,
  agendaDate,
  selected,
  onSelect,
  onDelete,
  onMove,
  onInclude,
  onClearOverride,
  subitems,
}: RowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const { activeTenant } = useTenant();
  const accent = getAccentColor(meetingType, activeTenant);

  const dur = item.timeRange?.duration ?? 0;
  let timeText: string | null = null;
  if (startMin !== null) {
    timeText =
      dur > 0 ? `${formatMinutes(startMin)} – ${formatMinutes(startMin + dur)}` : formatMinutes(startMin);
  } else if (item.timeRange?.start) {
    timeText = item.timeRange.start;
  }
  const durText = startMin === null && dur > 0 ? `${dur} min` : null;
  const hasSecondRow =
    item.recurring || !!item.leader || !!timeText || !!durText;
  const doesNotApply = item.recurring && item.appliesThisMeeting === false;
  const override = item.recurring ? occurrenceOverride(item, agendaDate) : undefined;
  const skipped = override === false;
  const included = override === true;
  const offSchedule = override === undefined && doesNotApply;

  return (
    <div
      ref={setNodeRef}
      style={
        isDragging
          ? { visibility: 'hidden' }
          : { transform: CSS.Translate.toString(transform), transition }
      }
    >
      <div
        className={`item-row${selected ? ' selected' : ''}${doesNotApply ? ' item-row-dimmed' : ''}`}
        onClick={() => onSelect(index)}
        title={doesNotApply ? 'Does not apply to this meeting' : undefined}
      >
        <button
          className="item-drag"
          aria-label="Reorder"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
        >
          <DragIcon />
        </button>

        <span className="item-badge">
          {item.emoji ? (
            <span className="item-emoji">{item.emoji}</span>
          ) : (
            <span className="item-type-badge" style={{ background: `color-mix(in srgb, ${accent} 12%, transparent)`, color: accent }}>
              {(item.title || '?').charAt(0).toUpperCase()}
            </span>
          )}
        </span>

        <span className="item-main">
          <span className="item-title">{item.title || 'Untitled'}</span>
          {hasSecondRow && (
            <span className="item-sub">
              {item.recurring && <span className="item-recur" title="Recurring">↻</span>}
              {skipped && <span className="item-skip-badge">Skipped</span>}
              {offSchedule && <span className="item-skip-badge">Not this meeting</span>}
              {included && <span className="item-include-badge">Included</span>}
              {item.leader && <span className="item-leader">{item.leader}</span>}
              {durText && <span>{durText}</span>}
              {timeText && <span className="item-time">{timeText}</span>}
            </span>
          )}
        </span>

        {offSchedule && (
          <button
            className="item-unskip"
            aria-label="Include this occurrence"
            title="Include this occurrence"
            onClick={(e) => {
              e.stopPropagation();
              onInclude(index);
            }}
          >
            <PlusIcon />
            Include
          </button>
        )}
        {skipped && (
          <button
            className="item-unskip"
            aria-label="Restore this occurrence"
            title="Restore this occurrence"
            onClick={(e) => {
              e.stopPropagation();
              onClearOverride(index);
            }}
          >
            <RestoreIcon />
            Restore
          </button>
        )}
        {included && (
          <button
            className="item-unskip"
            aria-label="Remove this occurrence"
            title="Remove this off-schedule occurrence"
            onClick={(e) => {
              e.stopPropagation();
              onClearOverride(index);
            }}
          >
            Remove
          </button>
        )}

        {!item.recurring && (
          <button
            className="item-move"
            aria-label="Move to another meeting"
            title="Move to another meeting"
            onClick={(e) => {
              e.stopPropagation();
              onMove(index);
            }}
          >
            <MoveIcon />
          </button>
        )}

        <button
          className="item-del"
          aria-label="Delete item"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(index);
          }}
        >
          <TrashIcon />
        </button>
      </div>
      {subitems.length > 0 && (
        <div className="subitem-group">
          {subitems.map((subitem, j) => (
            <SubRow key={j} subitem={subitem} onSelect={() => onSelect(index)} />
          ))}
        </div>
      )}
    </div>
  );
}

const SortableItemRow = memo(SortableItemRowComponent);

interface OverlayProps {
  item: Item;
  meetingType: MeetingType;
  startMin: number | null;
  subitems: Subitem[];
}

function ItemRowOverlay({ item, meetingType, startMin, subitems }: OverlayProps) {
  const { activeTenant } = useTenant();
  const accent = getAccentColor(meetingType, activeTenant);

  const dur = item.timeRange?.duration ?? 0;
  let timeText: string | null = null;
  if (startMin !== null) {
    timeText =
      dur > 0 ? `${formatMinutes(startMin)} – ${formatMinutes(startMin + dur)}` : formatMinutes(startMin);
  } else if (item.timeRange?.start) {
    timeText = item.timeRange.start;
  }
  const durText = startMin === null && dur > 0 ? `${dur} min` : null;
  const hasSecondRow = item.recurring || !!item.leader || !!timeText || !!durText;
  const doesNotApply = item.recurring && item.appliesThisMeeting === false;

  return (
    <>
      <div className={`item-row${doesNotApply ? ' item-row-dimmed' : ''}`} title={doesNotApply ? 'Does not apply to this meeting' : undefined}>
        <button className="item-drag" aria-label="Reorder" tabIndex={-1}>
          <DragIcon />
        </button>

        <span className="item-badge">
          {item.emoji ? (
            <span className="item-emoji">{item.emoji}</span>
          ) : (
            <span className="item-type-badge" style={{ background: `color-mix(in srgb, ${accent} 12%, transparent)`, color: accent }}>
              {(item.title || '?').charAt(0).toUpperCase()}
            </span>
          )}
        </span>

        <span className="item-main">
          <span className="item-title">{item.title || 'Untitled'}</span>
          {hasSecondRow && (
            <span className="item-sub">
              {item.recurring && <span className="item-recur" title="Recurring">↻</span>}
              {doesNotApply && <span className="item-skip-badge">Skipped</span>}
              {item.leader && <span className="item-leader">{item.leader}</span>}
              {durText && <span>{durText}</span>}
              {timeText && <span className="item-time">{timeText}</span>}
            </span>
          )}
        </span>

        <span className="item-del" style={{ visibility: 'hidden' }}>
          <TrashIcon />
        </span>
      </div>
      {subitems.length > 0 && (
        <div className="subitem-group">
          {subitems.map((subitem, j) => (
            <SubRow key={j} subitem={subitem} onSelect={() => {}} />
          ))}
        </div>
      )}
    </>
  );
}

interface SubRowProps {
  subitem: Subitem;
  onSelect: () => void;
}

function SubRow({ subitem, onSelect }: SubRowProps) {
  return (
    <div
      className="subitem-row"
      onClick={onSelect}
    >
      <span className="subitem-badge">
        {subitem.emoji ? (
          <span className="subitem-emoji">{subitem.emoji}</span>
        ) : (
          <span className="subitem-bullet">•</span>
        )}
      </span>
      <span className="subitem-title">{subitem.title}</span>
    </div>
  );
}

function GapBar({ minutes }: { minutes: number }) {
  const isOverlap = minutes < 0;
  const abs = Math.abs(minutes);
  const label = isOverlap ? `${abs} min overlap` : `${abs} min gap`;
  return (
    <div className={`gap-bar${isOverlap ? ' gap-bar-overlap' : ''}`} aria-label={label}>
      <span className="gap-bar-label">{label}</span>
    </div>
  );
}

function DragIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" />
      <circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" />
      <circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
    </svg>
  );
}

function MoveIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M14 5l4 4m0 0l-4 4m4-4H8M4 19V9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 7h14M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RestoreIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 12a8 8 0 1 0 2.3-5.6M4 4v3.5h3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
